import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 硬核科普 - 竖屏「数据可视化故事」风格
 *
 * 核心亮点：
 * - SVG 动态折线图（坐标轴 + 网格 + 曲线生长 + 面积填充）
 * - SVG 动态饼图（扇形逐段展开）
 * - 数字滚动计数器
 * - 发光粒子从数据点弹出
 * - 关键数据点标注动画
 *
 * 尺寸：1080x1920 竖屏
 */

interface LineDataPoint {
  x: string;
  y: number;
}

interface PieSlice {
  label: string;
  value: number;
  color: string;
}

interface ScienceDataVizProps {
  topic: string;
  subtitle: string;
  lineChart: { title: string; data: LineDataPoint[]; unit: string; color: string };
  pieChart: { title: string; slices: PieSlice[] };
  bigStat: { number: number; suffix: string; description: string };
  insight: string;
  source: string;
}

const DEFAULT_PROPS: ScienceDataVizProps = {
  topic: '全球可再生能源增长趋势',
  subtitle: 'Renewable Energy Growth 2015-2025',
  lineChart: {
    title: '全球可再生能源装机容量（GW）',
    data: [
      { x: '2015', y: 1850 },
      { x: '2017', y: 2180 },
      { x: '2019', y: 2590 },
      { x: '2021', y: 3060 },
      { x: '2023', y: 3870 },
      { x: '2025', y: 4800 },
    ],
    unit: 'GW',
    color: '#34d399',
  },
  pieChart: {
    title: '2025 年能源结构占比',
    slices: [
      { label: '太阳能', value: 35, color: '#fbbf24' },
      { label: '风能', value: 28, color: '#38bdf8' },
      { label: '水电', value: 22, color: '#818cf8' },
      { label: '其他', value: 15, color: '#a78bfa' },
    ],
  },
  bigStat: { number: 4800, suffix: 'GW', description: '2025 年全球可再生能源总装机容量' },
  insight: '过去 10 年可再生能源装机增长了 160%，太阳能增速最快',
  source: 'IRENA Global Renewables 2025',
};

const AnimatedCounter: React.FC<{ target: number; frame: number; start: number; duration: number }> = ({
  target, frame, start, duration,
}) => {
  const progress = interpolate(frame, [start, start + duration], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const eased = 1 - Math.pow(1 - progress, 3);
  const current = Math.floor(target * eased);
  return <>{current.toLocaleString()}</>;
};

export const ScienceDataViz: React.FC<Partial<ScienceDataVizProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Layout constants
  const chartW = 920;
  const chartH = 440;
  const chartPad = 80;
  const chartPadLeft = 140;

  // Phases
  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Big stat (40-100)
  const statStart = 40;
  const statScale = spring({ frame: frame - statStart, fps, from: 0.7, to: 1, config: { damping: 10, stiffness: 100 } });
  const statOpacity = interpolate(frame, [statStart, statStart + 15], [0, 1], { extrapolateRight: 'clamp' });

  // Line chart (100-260)
  const lineStart = 100;
  const lineDraw = interpolate(frame, [lineStart, lineStart + 120], [0, 1], { extrapolateRight: 'clamp' });
  const lineVisible = interpolate(frame, [lineStart, lineStart + 15], [0, 1], { extrapolateRight: 'clamp' });

  // Pie chart (270-400)
  const pieStart = 270;
  const pieProgress = interpolate(frame, [pieStart, pieStart + 80], [0, 1], { extrapolateRight: 'clamp' });

  // Insight (420-470)
  const insightOpacity = interpolate(frame, [420, 450], [0, 1], { extrapolateRight: 'clamp' });

  // Line chart calculations
  const maxY = Math.max(...p.lineChart.data.map(d => d.y));
  const minY = Math.min(...p.lineChart.data.map(d => d.y)) * 0.8;
  const yRange = maxY - minY;

  const linePoints = p.lineChart.data.map((d, i) => {
    const x = chartPadLeft + (i / (p.lineChart.data.length - 1)) * (chartW - chartPadLeft - chartPad);
    const y = chartH - chartPad - ((d.y - minY) / yRange) * (chartH - chartPad * 2);
    return { x, y, label: d.x, value: d.y };
  });

  const pathD = linePoints.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x},${pt.y}`).join(' ');
  const areaD = pathD + ` L${linePoints[linePoints.length - 1].x},${chartH - chartPad} L${linePoints[0].x},${chartH - chartPad} Z`;

  // Pie chart calculations
  const pieR = 180;
  const pieCx = 500;
  const pieCy = 200;
  const total = p.pieChart.slices.reduce((s, sl) => s + sl.value, 0);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(129,140,248,0.03) 1px, transparent 0)',
          backgroundSize: '36px 36px',
        }}
      />

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 70,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -20}px)`,
        }}
      >
        <div style={{ fontSize: 40, color: '#34d399', fontWeight: 600, letterSpacing: '0.15em', marginBottom: 10 }}>
          DATA STORY
        </div>
        <h1 style={{ fontSize: 60, fontWeight: 800, margin: '0 60px', lineHeight: 1.3 }}>{p.topic}</h1>
        <p style={{ fontSize: 38, color: 'rgba(255,255,255,0.35)', marginTop: 8, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== BIG STAT ========== */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: statOpacity,
          transform: `scale(${statScale})`,
        }}
      >
        <div
          style={{
            padding: '30px',
            background: 'rgba(52,211,153,0.06)',
            border: '1px solid rgba(52,211,153,0.2)',
            borderRadius: 24,
          }}
        >
          <div style={{ fontSize: 72, fontWeight: 900, color: '#34d399', fontVariantNumeric: 'tabular-nums' }}>
            <AnimatedCounter target={p.bigStat.number} frame={frame} start={statStart} duration={50} />
            <span style={{ fontSize: 36, marginLeft: 8, opacity: 0.8 }}>{p.bigStat.suffix}</span>
          </div>
          <div style={{ fontSize: 35, color: 'rgba(255,255,255,0.5)', marginTop: 8 }}>{p.bigStat.description}</div>
        </div>
      </div>

      {/* ========== LINE CHART ========== */}
      <div
        style={{
          position: 'absolute',
          top: 650,
          left: 60,
          right: 60,
          opacity: lineVisible,
        }}
      >
        <div style={{ fontSize: 40, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>{p.lineChart.title}</div>
        <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
            const y = chartH - chartPad - t * (chartH - chartPad * 2);
            return (
              <g key={i}>
                <line x1={chartPadLeft} y1={y} x2={chartW - chartPad} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                <text x={chartPadLeft - 10} y={y + 10} fill="rgba(255,255,255,0.25)" fontSize="28" textAnchor="end">
                  {Math.round(minY + t * yRange)}
                </text>
              </g>
            );
          })}

          {/* Area fill (gradient) */}
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={p.lineChart.color} stopOpacity="0.3" />
              <stop offset="100%" stopColor={p.lineChart.color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={areaD}
            fill="url(#areaGrad)"
            strokeDasharray={2000}
            strokeDashoffset={2000 * (1 - lineDraw)}
            opacity={lineDraw}
          />

          {/* Line */}
          <path
            d={pathD}
            fill="none"
            stroke={p.lineChart.color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={2000}
            strokeDashoffset={2000 * (1 - lineDraw)}
          />
          {/* Line glow */}
          <path
            d={pathD}
            fill="none"
            stroke={p.lineChart.color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={2000}
            strokeDashoffset={2000 * (1 - lineDraw)}
            opacity="0.15"
          />

          {/* Data points with pulse */}
          {linePoints.map((pt, i) => {
            const ptProgress = interpolate(lineDraw, [i / linePoints.length, (i + 0.5) / linePoints.length], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            if (ptProgress < 0.1) return null;
            const pulse = interpolate(Math.sin((frame - lineStart + i * 10) / 12), [-1, 1], [8, 16]);

            return (
              <g key={i} opacity={ptProgress}>
                <circle cx={pt.x} cy={pt.y} r={pulse} fill={p.lineChart.color} opacity="0.12" />
                <circle cx={pt.x} cy={pt.y} r="7" fill={p.lineChart.color} />
                <circle cx={pt.x} cy={pt.y} r="3.5" fill="white" />
                {/* Value label */}
                <text
                  x={pt.x}
                  y={pt.y - 28}
                  fill="white"
                  fontSize="28"
                  fontWeight="bold"
                  textAnchor="middle"
                  opacity={ptProgress}
                >
                  {pt.value}
                </text>
                {/* X label */}
                <text
                  x={pt.x}
                  y={chartH - chartPad + 36}
                  fill="rgba(255,255,255,0.4)"
                  fontSize="28"
                  textAnchor="middle"
                >
                  {pt.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* ========== PIE CHART ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1210,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 40, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>{p.pieChart.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
          <svg width={pieCx} height={pieCy * 2} viewBox={`0 0 ${pieCx} ${pieCy * 2}`}>
            {(() => {
              let cumulativeAngle = -Math.PI / 2;
              return p.pieChart.slices.map((slice, i) => {
                const sliceAngle = (slice.value / total) * 2 * Math.PI;
                const drawAngle = sliceAngle * pieProgress;
                const startAngle = cumulativeAngle;
                const endAngle = startAngle + drawAngle;
                cumulativeAngle += sliceAngle;

                const cx = pieCx / 2;
                const cy = pieCy;
                const x1 = cx + pieR * Math.cos(startAngle);
                const y1 = cy + pieR * Math.sin(startAngle);
                const x2 = cx + pieR * Math.cos(endAngle);
                const y2 = cy + pieR * Math.sin(endAngle);
                const largeArc = drawAngle > Math.PI ? 1 : 0;

                const d = [
                  `M${cx},${cy}`,
                  `L${x1},${y1}`,
                  `A${pieR},${pieR} 0 ${largeArc} 1 ${x2},${y2}`,
                  'Z',
                ].join(' ');

                return <path key={i} d={d} fill={slice.color} opacity="0.85" />;
              });
            })()}
            {/* Center hole */}
            <circle cx={pieCx / 2} cy={pieCy} r={pieR * 0.5} fill="#0f172a" />
            <text
              x={pieCx / 2}
              y={pieCy - 14}
              fill="white"
              fontSize="30"
              fontWeight="900"
              textAnchor="middle"
              opacity={pieProgress}
            >
              100%
            </text>
            <text
              x={pieCx / 2}
              y={pieCy + 28}
              fill="rgba(255,255,255,0.4)"
              fontSize="30"
              textAnchor="middle"
              opacity={pieProgress}
            >
              可再生能源
            </text>
          </svg>

          {/* Legend */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {p.pieChart.slices.map((slice, i) => {
              const opacity = interpolate(frame, [pieStart + i * 20, pieStart + i * 20 + 15], [0, 1], { extrapolateRight: 'clamp' });
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, opacity }}>
                  <div style={{ width: 20, height: 20, borderRadius: 5, background: slice.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 38, color: 'rgba(255,255,255,0.8)' }}>{slice.label}</span>
                  <span style={{ fontSize: 38, fontWeight: 700, color: slice.color }}>{slice.value}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ========== INSIGHT ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 90,
          left: 60,
          right: 60,
          opacity: insightOpacity,
        }}
      >
        <div
          style={{
            padding: '22px 28px',
            background: 'rgba(52,211,153,0.06)',
            border: '1px solid rgba(52,211,153,0.2)',
            borderRadius: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span style={{ fontSize: 28 }}>💡</span>
          <span style={{ fontSize: 32, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>{p.insight}</span>
        </div>
      </div>

      {/* Source */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: insightOpacity * 0.6,
        }}
      >
        <span style={{ fontSize: 32, color: 'rgba(255,255,255,0.25)' }}>📎 {p.source}</span>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #34d399, #818cf8)',
        }}
      />
    </AbsoluteFill>
  );
};
